import { useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Store, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

// Store switcher dropdown. Compact "pill" variant lives in the header;
// wider variant appears in the user dropdown menu.
export const StoreSwitcher = ({ variant = "header" }) => {
  const { user, switchStore } = useAuth();
  const [switching, setSwitching] = useState(false);

  const stores = useMemo(() => user?.stores || [], [user]);
  const activeStore = useMemo(
    () => stores.find((s) => s.id === user?.active_store_id) || stores[0],
    [stores, user],
  );

  // Multi-store switcher is admin-gated: only visible to users who have been
  // assigned 2+ stores by an admin. Users with 1 store see nothing — the
  // switcher (and the whole concept) is invisible to them until an admin
  // assigns them a second store via Admin Panel → Users → Edit → Stores.
  if (!user || stores.length < 2) return null;

  const handleSwitch = async (storeId) => {
    if (storeId === user?.active_store_id) return;
    setSwitching(true);
    try {
      await switchStore(storeId);
      const target = stores.find((s) => s.id === storeId);
      toast.success(`Switched to ${target?.name || "store"}`);
      // Reload page so all data fetches re-fire under the new active store.
      // (Simpler and safer than trying to invalidate every page's cache manually.)
      setTimeout(() => window.location.reload(), 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to switch store");
    } finally {
      setSwitching(false);
    }
  };

  const label = activeStore?.name?.trim() || "Unnamed store";

  if (variant === "menu") {
    // Rendered inside the user dropdown menu
    return (
      <div className="px-2 py-1.5" data-testid="store-switcher-menu">
        <p className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider mb-1">Switch store</p>
        <div className="flex flex-col gap-1">
          {stores.map((s) => {
            const isActive = s.id === user?.active_store_id;
            return (
              <button
                key={s.id}
                onClick={() => handleSwitch(s.id)}
                disabled={switching}
                className={`text-left px-2 py-1.5 rounded-md text-xs font-mono flex items-center gap-2 transition-colors ${
                  isActive
                    ? "bg-primary/20 text-primary"
                    : "hover:bg-secondary text-card-foreground"
                }`}
                data-testid={`store-switch-menu-${s.id}`}
              >
                {isActive ? (
                  <Check className="w-3 h-3 flex-shrink-0" />
                ) : (
                  <Store className="w-3 h-3 flex-shrink-0 opacity-60" />
                )}
                <div className="flex flex-col min-w-0">
                  <span className="truncate font-semibold">{s.name?.trim() || "Unnamed"}</span>
                  {s.address?.trim() && (
                    <span className="text-[10px] text-muted-foreground truncate">{s.address}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Header variant — compact pill
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative z-20 h-9 gap-2 px-3 rounded-full border border-border/40 bg-secondary/40 hover:bg-secondary max-w-[220px] flex-shrink-0"
          disabled={switching}
          data-testid="store-switcher-header"
        >
          <Store className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span className="hidden sm:inline font-mono text-xs text-card-foreground truncate" title={label}>
            {label}
          </span>
          <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Your stores ({stores.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {stores.map((s) => {
          const isActive = s.id === user?.active_store_id;
          return (
            <DropdownMenuItem
              key={s.id}
              onClick={() => handleSwitch(s.id)}
              className="flex items-start gap-2 cursor-pointer"
              data-testid={`store-switch-header-${s.id}`}
            >
              <div className="pt-0.5">
                {isActive ? (
                  <Check className="w-3.5 h-3.5 text-primary" />
                ) : (
                  <Store className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className={`font-mono text-sm truncate ${isActive ? "font-bold text-primary" : ""}`}>
                  {s.name?.trim() || "Unnamed store"}
                </span>
                {s.address?.trim() && (
                  <span className="text-[10px] text-muted-foreground truncate">{s.address}</span>
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StoreSwitcher;
